import styled from "@emotion/styled";
import { Drawer, Paper } from "@mui/material";
import { lighten } from "@mui/material/styles";

import { identifiersData } from "@/data";

const sidebarWidth = 240;

const flyoutRadius = 12;

const flyoutWidth = 200;

export const sidebarItemHeight = 40;

// A child row reuses its parent's columns — same start padding, same icon slot — so that the dot standing in for
// the icon lands directly under the parent's icon and the two labels share a start edge. This matches the width a
// parent's `ListItemIcon` takes from the icon inside it.
const sidebarIconSlotSize = 24;

const childDotSize = 4;

// The title strip stands taller than a rail row so it reads as the group's heading rather than another row.
const flyoutTitleHeight = 48;

// How far the end shadow is allowed to escape the clip that hides the panel behind the rail.
const flyoutShadowBleed = 32;

// These tints must be opaque, not alpha. The drawer paper is white while the flyout sits on `background.default`,
// so a translucent green composites to two different colours across the two surfaces. Lightening towards white by
// `1 - opacity` yields the same shade a translucent tint produced over the white drawer, now backdrop-independent.
const highlightLightening = 0.9;

// Half the tint's strength, so a hovered child sits under its parent in the hierarchy rather than competing with it.
const childHighlightLightening = 0.95;

const lightenBrand = (theme, coefficient) =>
    theme?.palette?.primary?.main ? lighten(theme.palette.primary.main, coefficient) : undefined;

// Shared by the flyout's title strip and a hovered sidebar row, so the two read as exactly the same colour.
const sidebarHighlight = (theme) => lightenBrand(theme, highlightLightening);

const sidebarChildHighlight = (theme) => lightenBrand(theme, childHighlightLightening);

// Children render in two places — inline in the drawer and in the flyout off the collapsed rail — so both surfaces
// carry this rule. The `:not()` keeps it from also claiming the parent rows it sits among.
const sidebarChildMixin = (theme) => ({
    // Children sit on white of their own rather than showing the surface behind them, which in the flyout is the
    // greyer `background.default`.
    [`& .${identifiersData.classNames.sidebarChild}`]: { backgroundColor: theme?.palette?.background?.paper },
    [`& .${identifiersData.classNames.sidebarChild}:hover`]: { backgroundColor: sidebarChildHighlight(theme) },
});

const openedMixin = (theme) => ({
    marginTop: 0,
    overflowX: "hidden",
    transition: theme?.transitions?.create("width", {
        duration: theme?.transitions?.duration?.enteringScreen,
        easing: theme?.transitions?.easing?.sharp,
    }),
    width: `${sidebarWidth}px!important`,
});

const closedMixin = (theme) => ({
    marginTop: 0,
    overflowX: "hidden",
    transition: theme?.transitions?.create("width", {
        duration: theme?.transitions?.duration?.leavingScreen,
        easing: theme?.transitions?.easing?.sharp,
    }),
    [theme?.breakpoints?.up("sm")]: { width: theme?.spacing ? `calc(${theme?.spacing(8)} + 1px)` : "initial" },
    width: theme?.spacing ? `calc(${theme?.spacing(7)} + 1px)` : "initial",
});

// Fills the icon slot so the child's label starts where its parent's does, and centres the dot itself within it.
// The active colour is named rather than inherited: the enclosing `ListItemIcon` sets a colour of its own, so an
// inherited dot would pick up that grey instead of the brand green the active row's label carries.
export const StyledSidebarChildDot = styled("span")(({ theme }) => ({
    alignItems: "center",
    color: theme?.palette?.grey?.[600],
    display: "flex",
    justifyContent: "center",
    width: sidebarIconSlotSize,
    "&::before": {
        backgroundColor: "currentColor",
        borderRadius: "50%",
        content: '""',
        height: childDotSize,
        width: childDotSize,
    },
    [`.${identifiersData.classNames.active} &`]: { color: theme?.palette?.primary?.main },
}));

// Clips everything left of the seam, so the panel is invisible until it clears the rail and appears to slide out
// from behind the sidebar. The other three sides are opened up to let the end shadow through.
export const StyledSidebarFlyoutClip = styled("div")({
    clipPath: `inset(-${flyoutShadowBleed}px -${flyoutShadowBleed}px -${flyoutShadowBleed}px 0)`,
});

// The collapsed rail's flyout reads as the sidebar widening rather than as a detached menu: it butts flush against
// the rail with square left corners, and only the free edge is rounded and shadowed.
export const StyledSidebarFlyout = styled(Paper)(({ theme }) => ({
    backgroundColor: theme?.palette?.background?.default,
    backgroundImage: "none",
    borderBottomRightRadius: flyoutRadius,
    borderTopRightRadius: flyoutRadius,
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
    // The negative spread cancels the blur on every edge but the end, so nothing bleeds back across the seam.
    boxShadow: "8px 0 16px -8px rgba(0, 0, 0, 0.24)",
    minWidth: flyoutWidth,
    overflow: "hidden",
    // No vertical padding: the title strip and the rows run edge to edge, leaving no uncoloured band.
    padding: 0,
    // The link that makes the title clickable fills the strip, so the strip itself only sets the row's shape —
    // padding lives on that inner link (or, route-less, on the fallback label) instead.
    "& header": {
        alignItems: "stretch",
        backgroundColor: sidebarHighlight(theme),
        display: "flex",
        minHeight: flyoutTitleHeight,
    },
    ...sidebarChildMixin(theme),
    // The flyout is portalled out of the drawer, so it needs its own copy of the sidebar's active-tab rule.
    [`& .MuiButtonBase-root.${identifiersData.classNames.active}`]: { color: theme?.palette?.primary?.main },
}));

export const StyledSidebarHeader = styled("div")(({ theme }) => ({
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: theme?.spacing ? theme?.spacing(0, 1) : "initial",
    ...theme?.mixins?.toolbar,
}));

export const StyledSidebar = styled(Drawer, { shouldForwardProp: (prop) => prop !== identifiersData.props.open })(
    ({ open, theme }) => ({
        boxSizing: "border-box",
        flexShrink: 0,
        whiteSpace: "nowrap",
        width: sidebarWidth,
        ...(open && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            "& .MuiDrawer-paper": closedMixin(theme),
        }),
        [[
            `& .MuiListItemButton-root:not(.${identifiersData.classNames.sidebarChild}):hover`,
            `& .MuiListItemButton-root.${identifiersData.classNames.sidebarFlyoutOpen}`,
        ].join(", ")]: { backgroundColor: sidebarHighlight(theme) },
        ...sidebarChildMixin(theme),
        [`& .MuiButtonBase-root.${identifiersData.classNames.active}`]: { color: theme?.palette?.primary?.main },
    }),
);
